import React from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import getShipmentOptions from "../../../../../hooks/getShipmentOptions";
import { ShipmentContainerInfo } from "../../../../../models/shipments";
import { handleGetConvertValue } from "../../../../../util/converts";
import { getMeasurement, getWeightMeasurement } from "../../../../../util/getMeasurement";
import useStyles, { ViewWrapper } from "./styles";

interface DetailsViewProps {
  isAirType?: boolean;
  shipmentContainer?: ShipmentContainerInfo;
}

// GRID SPACING => SPACING * 8px
export function ContainerDetailsView({ isAirType = false, shipmentContainer }: DetailsViewProps) {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { containerTypeOptions } = getShipmentOptions();

  const renderField = (label: string, value?: string) => {
    return (
      <ViewWrapper>
        <label>{translate(label)}</label>
        <span>{value || "--"}</span>
      </ViewWrapper>
    );
  };

  const renderVolumeValue = (label: string, value?: string | number) => {
    const measurement = getMeasurement();
    const convertedValue = handleGetConvertValue(
      Number(value),
      measurement?.code,
      measurement?.decimalPlaces
    );

    return renderField(label, `${convertedValue} ${measurement?.code?.toUpperCase()}`);
  };

  const renderWeightValue = (label: string, value?: string | number, extraValue?: string) => {
    const measurementWeight = getWeightMeasurement();
    const convertedValue = handleGetConvertValue(
      Number(value ?? 0),
      measurementWeight?.code,
      measurementWeight?.decimalPlaces
    );

    return renderField(
      label,
      `${convertedValue} ${measurementWeight?.code?.toUpperCase()} ${extraValue ?? ""}`
    );
  };

  const returnContainerTypeLabel = (containerType?: string) => {
    if (!containerType) return "--";

    const shipmentContainerType = containerTypeOptions.find(
      (option) => option.value === containerType
    );

    if (shipmentContainerType) return shipmentContainerType.label;

    return "--";
  };

  return (
    <Grid className={classes.wrapper} container direction="column" rowSpacing={3}>
      <Grid item container direction="row" rowSpacing={1}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          {renderField("containerCase", shipmentContainer?.container)}
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          {renderField("containerCaseSize", returnContainerTypeLabel(shipmentContainer?.size))}
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          {renderField("sealMarkings", shipmentContainer?.seal)}
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          {renderWeightValue(
            "grossWeight",
            shipmentContainer?.grossWeight || "0",
            shipmentContainer?.verifiedGrossWeight
              ? `(${translate("verifiedGrossWeight")})`
              : undefined
          )}
        </Grid>
      </Grid>
      <Grid item container direction="row" rowSpacing={1}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          {renderWeightValue("netWeight", shipmentContainer?.netWeight || "0")}
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          {renderWeightValue("tareWeight", shipmentContainer?.tareWeight || "0")}
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          {renderVolumeValue("grossVolume", shipmentContainer?.grossVolume || "0")}
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          {renderVolumeValue(
            isAirType ? "dimensionalWeight" : "volumetricWeight",
            shipmentContainer?.volumetricWeight || "0"
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}
